// =============================================================================
// MeuDriver — Instructor Payouts (Batch J2)
// =============================================================================

import { useState } from 'react'
import {
  Wallet,
  CheckCircle2,
  Clock,
  XCircle,
  Loader2,
  AlertTriangle,
  RefreshCw,
  Link as LinkIcon,
} from 'lucide-react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Separator } from '@/components/ui/separator'
import { formatCurrency, formatDate } from '@/lib/utils'
import {
  useInstructorPayouts,
  useRequestPayout,
  useInstructorMpAccount,
  useSaveInstructorMpAccount,
  type InstructorPayout,
} from '@/hooks/usePayouts'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function statusLabel(status: InstructorPayout['status']): string {
  switch (status) {
    case 'pending':    return 'Pendente'
    case 'processing': return 'Processando'
    case 'completed':  return 'Concluído'
    case 'failed':     return 'Falhou'
  }
}

function statusBadge(status: InstructorPayout['status']) {
  switch (status) {
    case 'pending':
      return <Badge variant="secondary" className="gap-1"><Clock className="h-3 w-3" />Pendente</Badge>
    case 'processing':
      return <Badge className="gap-1 bg-blue-100 text-blue-700 border-blue-200"><Loader2 className="h-3 w-3 animate-spin" />Processando</Badge>
    case 'completed':
      return <Badge className="gap-1 bg-emerald-100 text-emerald-700 border-emerald-200"><CheckCircle2 className="h-3 w-3" />Concluído</Badge>
    case 'failed':
      return <Badge variant="destructive" className="gap-1"><XCircle className="h-3 w-3" />Falhou</Badge>
  }
}

// ---------------------------------------------------------------------------
// Schemas
// ---------------------------------------------------------------------------

const payoutSchema = z.object({
  amount: z
    .string()
    .min(1, 'Informe o valor')
    .refine((v) => !isNaN(parseFloat(v.replace(',', '.'))) && parseFloat(v.replace(',', '.')) >= 10, {
      message: 'Valor mínimo: R$ 10,00',
    }),
})

const mpAccountSchema = z.object({
  mp_user_id: z.string().min(1, 'Informe seu MP User ID').regex(/^\d+$/, 'Deve conter apenas números'),
  mp_email:   z.string().email('E-mail inválido').optional().or(z.literal('')),
})

type PayoutFormValues   = z.infer<typeof payoutSchema>
type MpAccountFormValues = z.infer<typeof mpAccountSchema>

// ---------------------------------------------------------------------------
// Sub-componentes
// ---------------------------------------------------------------------------

function PayoutsTable({ payouts, isLoading }: { payouts: InstructorPayout[]; isLoading: boolean }) {
  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-14 w-full rounded-lg" />
        ))}
      </div>
    )
  }

  if (payouts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <Wallet className="h-12 w-12 text-muted-foreground/40 mb-3" />
        <p className="text-sm text-muted-foreground">Nenhum saque solicitado ainda.</p>
      </div>
    )
  }

  return (
    <div className="divide-y divide-border">
      {payouts.map((payout) => (
        <div key={payout.id} className="flex items-center justify-between py-4 gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-foreground">
                {formatCurrency(payout.amount)}
              </span>
              {statusBadge(payout.status)}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              Solicitado em {formatDate(payout.requested_at)}
              {payout.processed_at && ` · Processado em ${formatDate(payout.processed_at)}`}
            </p>
            {payout.error_message && (
              <p className="text-xs text-destructive mt-0.5 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3 shrink-0" />
                {payout.error_message}
              </p>
            )}
          </div>
          {payout.mp_transfer_id && (
            <span className="text-xs text-muted-foreground font-mono truncate max-w-[120px]" title={payout.mp_transfer_id}>
              MP: {payout.mp_transfer_id}
            </span>
          )}
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Página principal
// ---------------------------------------------------------------------------

export default function InstructorPayouts() {
  const [payoutDialogOpen, setPayoutDialogOpen] = useState(false)
  const [mpDialogOpen,     setMpDialogOpen]     = useState(false)

  const { data: payouts = [], isLoading: payoutsLoading } = useInstructorPayouts()
  const { data: mpAccount, isLoading: mpLoading }         = useInstructorMpAccount()

  const requestPayout = useRequestPayout()
  const saveMpAccount = useSaveInstructorMpAccount()

  const payoutForm = useForm<PayoutFormValues>({
    resolver: zodResolver(payoutSchema),
    defaultValues: { amount: '' },
  })

  const mpForm = useForm<MpAccountFormValues>({
    resolver: zodResolver(mpAccountSchema),
    defaultValues: {
      mp_user_id: mpAccount?.mp_user_id ?? '',
      mp_email:   mpAccount?.mp_email   ?? '',
    },
  })

  // Atualiza defaults do form MP quando a conta carrega
  const mpUserIdWatch = mpForm.watch('mp_user_id')
  if (mpAccount && !mpUserIdWatch) {
    mpForm.reset({ mp_user_id: mpAccount.mp_user_id, mp_email: mpAccount.mp_email ?? '' })
  }

  async function onSubmitPayout(values: PayoutFormValues) {
    if (!mpAccount?.mp_user_id) {
      toast.error('Vincule sua conta Mercado Pago antes de solicitar um saque.')
      setPayoutDialogOpen(false)
      setMpDialogOpen(true)
      return
    }

    const amount = parseFloat(values.amount.replace(',', '.'))

    requestPayout.mutate(
      { amount, mp_recipient_id: mpAccount.mp_user_id },
      {
        onSuccess: () => {
          toast.success('Saque solicitado com sucesso!')
          setPayoutDialogOpen(false)
          payoutForm.reset()
        },
        onError: (err) => {
          toast.error(err.message)
        },
      },
    )
  }

  async function onSubmitMpAccount(values: MpAccountFormValues) {
    saveMpAccount.mutate(
      { mp_user_id: values.mp_user_id, mp_email: values.mp_email || undefined },
      {
        onSuccess: () => {
          toast.success('Conta Mercado Pago vinculada!')
          setMpDialogOpen(false)
        },
        onError: (err) => {
          toast.error(err.message)
        },
      },
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Saques</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Gerencie seus saques e conta Mercado Pago
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => setMpDialogOpen(true)}
            data-testid="btn-vincular-mp"
          >
            <LinkIcon className="h-4 w-4" />
            {mpAccount ? 'Editar conta MP' : 'Vincular conta MP'}
          </Button>
          <Button
            size="sm"
            className="gap-2"
            onClick={() => setPayoutDialogOpen(true)}
            data-testid="btn-solicitar-saque"
          >
            <Wallet className="h-4 w-4" />
            Solicitar saque
          </Button>
        </div>
      </div>

      {/* Conta MP */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <LinkIcon className="h-4 w-4" />
            Conta Mercado Pago
          </CardTitle>
        </CardHeader>
        <CardContent>
          {mpLoading ? (
            <Skeleton className="h-10 w-48" />
          ) : mpAccount ? (
            <div className="flex items-center gap-3">
              <div>
                <p className="text-sm font-medium">MP User ID: <span className="font-mono">{mpAccount.mp_user_id}</span></p>
                {mpAccount.mp_email && (
                  <p className="text-xs text-muted-foreground">{mpAccount.mp_email}</p>
                )}
              </div>
              {mpAccount.verified && (
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">
                  <CheckCircle2 className="h-3 w-3 mr-1" />Verificado
                </Badge>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Nenhuma conta MP vinculada. Vincule para solicitar saques.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Histórico */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <RefreshCw className="h-4 w-4" />
              Histórico de saques
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <PayoutsTable payouts={payouts} isLoading={payoutsLoading} />
        </CardContent>
      </Card>

      {/* Dialog: solicitar saque */}
      <Dialog open={payoutDialogOpen} onOpenChange={setPayoutDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Solicitar saque</DialogTitle>
            <DialogDescription>
              O valor será transferido para sua conta Mercado Pago vinculada.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={payoutForm.handleSubmit(onSubmitPayout)} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="payout-amount">Valor (R$)</Label>
              <Input
                id="payout-amount"
                placeholder="0,00"
                data-testid="input-payout-amount"
                {...payoutForm.register('amount')}
              />
              {payoutForm.formState.errors.amount && (
                <p className="text-xs text-destructive">
                  {payoutForm.formState.errors.amount.message}
                </p>
              )}
            </div>

            {!mpAccount && (
              <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 rounded-lg p-3">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                Você precisa vincular sua conta MP antes de solicitar saques.
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setPayoutDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={requestPayout.isPending}
                data-testid="btn-confirmar-saque"
              >
                {requestPayout.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Confirmar saque
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: vincular conta MP */}
      <Dialog open={mpDialogOpen} onOpenChange={setMpDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Vincular conta Mercado Pago</DialogTitle>
            <DialogDescription>
              Informe seu MP User ID para receber saques. Você encontra esse ID no painel do Mercado Pago em Configuracoes &gt; Dados da conta.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={mpForm.handleSubmit(onSubmitMpAccount)} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="mp-user-id">MP User ID</Label>
              <Input
                id="mp-user-id"
                placeholder="Ex: 123456789"
                data-testid="input-mp-user-id"
                {...mpForm.register('mp_user_id')}
              />
              {mpForm.formState.errors.mp_user_id && (
                <p className="text-xs text-destructive">
                  {mpForm.formState.errors.mp_user_id.message}
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="mp-email">E-mail do MP (opcional)</Label>
              <Input
                id="mp-email"
                type="email"
                placeholder="seu@email.com"
                data-testid="input-mp-email"
                {...mpForm.register('mp_email')}
              />
              {mpForm.formState.errors.mp_email && (
                <p className="text-xs text-destructive">
                  {mpForm.formState.errors.mp_email.message}
                </p>
              )}
            </div>
            <Separator />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setMpDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={saveMpAccount.isPending}
                data-testid="btn-salvar-mp"
              >
                {saveMpAccount.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Salvar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
